import React, { useState } from 'react'
import { Layout, Menu, Dropdown, Button } from 'antd'
import { LogoutOutlined, SettingOutlined, UserOutlined, DownOutlined, HistoryOutlined, FileDoneOutlined, TranslationOutlined } from '@ant-design/icons'
import Logo from '../common/Logo'
import SettingsModal from './Settings'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { AuthContext } from '../auth/AuthContext'

const { Sider, Header } = Layout
const HEADER_HEIGHT = 95
const LANGS = [
  { key: 'fr', label: 'Français' },
  { key: 'en', label: 'English' }
]

const DashboardLayout = ({ children, selectedMenuKey = "ongoing" }) => {
  const [settingsOpen, setSettingsOpen] = useState(false)
  const navigate = useNavigate()
  const { logout, user } = React.useContext(AuthContext)
  const { t, i18n } = useTranslation()

  // Language dropdown menu items
  const langMenuItems = LANGS.map(lang => ({
    key: lang.key,
    label: lang.label
  }))
  const langMenu = {
    items: langMenuItems,
    selectedKeys: [i18n.language],
    onClick: ({ key }) => i18n.changeLanguage(key)
  }

  // Profile menu items
  const profileMenu = {
    items: [
      {
        key: 'settings',
        icon: <SettingOutlined />,
        label: t('settings'),
        onClick: () => setSettingsOpen(true)
      },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: t('logout'),
        onClick: () => {
          logout()
          navigate('/login')
        }
      }
    ]
  }

  // Sidebar menu handler
  const handleMenuClick = ({ key }) => {
    if (key === "history") {
      navigate("/history")
    } else if (key === "ongoing") {
      navigate("/dashboard")
    }
  }

  return (
    <>
      <div style={{ display: 'flex', height: '100vh', minHeight: '100vh' }}>
        <Sider
          width={220}
          style={{
            background: '#fff',
            borderRight: '1px solid #f0f0f0',
            position: 'relative',
            height: '100vh',
            minHeight: '100vh',
            padding: 0,
            flexShrink: 0,
            display: 'block'
          }}
        >
          <div style={{
            height: HEADER_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 32,
          }}>
            <Logo size={200} />
          </div>
          <Menu
            mode="inline"
            selectedKeys={[selectedMenuKey]}
            style={{ borderRight: 0 }}
            items={[
              {
                key: 'ongoing',
                icon: <FileDoneOutlined />,
                label: t('my_rfq_requests'),
              },
              {
                key: 'history',
                icon: <HistoryOutlined />,
                label: t('history')
              },
            ]}
            onClick={handleMenuClick}
          />
          {/* Bottom bar absolutely positioned at bottom */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            width: '100%',
            padding: '0 0 32px 0',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            alignItems: 'center',
            background: '#fff'
          }}>
            <Dropdown
              menu={langMenu}
              trigger={['click']}
              placement="topLeft"
              arrow
            >
              <Button
                style={{
                  width: '80%',
                  fontWeight: 500,
                  borderColor: '#d9d9d9',
                  background: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  textAlign: 'center'
                }}
                block
                className="language-button"
              >
                <TranslationOutlined />
                {t('language')}
              </Button>
            </Dropdown>
            <Button
              type="default"
              danger
              icon={<LogoutOutlined />}
              onClick={() => {
                logout()
                navigate('/login')
              }}
              style={{
                width: '80%',
                borderColor: '#ff4d4f',
                color: '#ff4d4f',
                fontWeight: 500,
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                textAlign: 'center'
              }}
              className="logout-outline-red"
              block
            >
              {t('logout')}
            </Button>
          </div>
        </Sider>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Header
            style={{
              background: '#fff',
              padding: '0 24px',
              display: 'flex',
              alignItems: 'center',
              minHeight: HEADER_HEIGHT,
              height: HEADER_HEIGHT,
              borderBottom: '1px solid #f0f0f0',
              justifyContent: 'space-between',
              flexShrink: 0
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              height: '100%'
            }}>
              <span style={{
                fontSize: 22,
                fontWeight: 700,
                color: '#222',
                letterSpacing: 0.5,
                textShadow: "0 1px 4px #e3eaf2"
              }}>
                {t('title')}
              </span>
            </div>
            <Dropdown menu={profileMenu} trigger={['click']} placement="bottomRight" overlayStyle={{ marginTop: 14 }}>
              <Button
                type="text"
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  paddingRight: 12,
                  paddingLeft: 14,
                  paddingTop: 40,
                  paddingBottom: 30,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  minWidth: 160,
                  textAlign: 'left',
                  lineHeight: '1.2'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <UserOutlined style={{ fontSize: 22, marginRight: 8, color: '#1890ff' }} />
                  {user?.vendor_name || user?.name || user?.username || t('user')}
                  <DownOutlined style={{ fontSize: 16, marginLeft: 6 }} />
                </span>
                {user?.email && (
                  <span style={{ color: '#888', fontSize: 14, margin: 0, marginTop: 2, marginBottom: 10 }}>
                    {user.email}
                  </span>
                )}
              </Button>
            </Dropdown>
          </Header>
          <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            {children}
          </div>
        </div>
      </div>
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <style>
        {`
          .logout-outline-red:hover, .logout-outline-red:focus {
            background: #ff4d4f !important;
            color: #fff !important;
            border-color: #ff4d4f !important;
          }
        `}
      </style>
    </>
  )
}

export default DashboardLayout
